import React, { useContext, useState, useEffect } from 'react';
import { UserContext } from '../App';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

export default function Profile({ embedded = false }) {
  const { currentUser, setCurrentUser, setUsers, users } = useContext(UserContext);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 700);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 700);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Split name into first and last if possible
    const [first, ...rest] = (currentUser.name || '').split(' ');
    setFirstName(first || '');
    setLastName(rest.join(' ') || '');
    setEmail(currentUser.email || '');
    setMarketingConsent(!!currentUser.marketingConsent);
  }, [currentUser]);

  const handleSave = async e => {
    e.preventDefault();
    setError('');
    if (password && password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    // Only send fields that have changed
    const updateData = { id: currentUser.id };
    const fullName = `${firstName} ${lastName}`.trim();
    if (fullName !== (currentUser.name || '')) updateData.name = fullName;
    if (email !== currentUser.email) updateData.email = email;
    if (password) updateData.password = password;
    updateData.marketingConsent = marketingConsent;
    updateData.marketingConsentVersion = '2025-09-01';
    if (!updateData.name && !updateData.email && !updateData.password) {
      // Still allow saving marketing updates alone
      if (typeof updateData.marketingConsent !== 'boolean') {
        setError('No changes to save');
        return;
      }
    }
    try {
      await api.put('/auth/user', updateData);
      // Update local context
      setCurrentUser({ ...currentUser, name: fullName, email, marketingConsent });
      setUsers(users => users.map(u => u.id === currentUser.id ? { ...u, name: fullName, email, marketingConsent } : u));
      setSaved(true);
      setPassword('');
      setConfirmPassword('');
      setTimeout(() => setSaved(false), 1500);
    } catch (err) {
      setError('Failed to update profile');
    }
  };

  // Sidebar styles
  const sidebarStyle = {
    width: 220,
    background: 'var(--dusk-700)',
    color: '#fff',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'stretch',
    padding: '32px 0 18px 0',
    boxShadow: '2px 0 12px rgba(30,40,60,0.04)',
    position: 'fixed',
    left: 0,
    top: 0,
    height: '100vh',
    zIndex: 100,
  };
  const mobileSidebarStyle = {
    ...sidebarStyle,
    width: 180,
    minWidth: 0,
    maxWidth: '80vw',
    transition: 'transform 0.3s',
    transform: 'translateX(-100%)',
  };
  const mobileSidebarOpenStyle = {
    ...mobileSidebarStyle,
    transform: 'translateX(0)',
  };

  if (embedded) {
    return (
      <div style={{ padding: 20 }}>
        <div style={{ maxWidth: 800, margin: '0 auto', background: '#fff', borderRadius: 12, boxShadow: '0 4px 24px rgba(30,40,60,0.10)', padding: 32 }}>
          <h2 style={{ marginBottom: 24, fontWeight: 700, fontSize: 24, color: '#222' }}>Account Profile</h2>
          <div style={{ marginBottom: 32 }}>
            <h3 style={{ fontWeight: 600, fontSize: 20, marginBottom: 16 }}>Profile details</h3>
            <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontWeight: 500, color: '#333' }}>First Name</label>
                  <input value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="First Name" style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: '1px solid #ccc', fontSize: 16 }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontWeight: 500, color: '#333' }}>Last Name</label>
                  <input value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Last Name" style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: '1px solid #ccc', fontSize: 16 }} />
                </div>
              </div>
              <div>
                <label style={{ fontWeight: 500, color: '#333' }}>Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: '1px solid #ccc', fontSize: 16 }} />
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1, position: 'relative' }}>
                  <label style={{ fontWeight: 500, color: '#333' }}>New Password</label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="New Password"
                    style={{ width: '100%', padding: '10px 36px 10px 12px', borderRadius: 6, border: '1px solid #ccc', fontSize: 16, boxSizing: 'border-box' }}
                  />
                  <span
                    onClick={() => setShowPassword(s => !s)}
                    style={{ position: 'absolute', right: 10, top: 38, cursor: 'pointer', color: '#888', fontSize: 18 }}
                    title={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </span>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontWeight: 500, color: '#333' }}>Confirm Password</label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    placeholder="Confirm Password"
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: '1px solid #ccc', fontSize: 16, boxSizing: 'border-box' }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <input id="marketing-embedded" type="checkbox" checked={marketingConsent} onChange={e => setMarketingConsent(e.target.checked)} />
                <label htmlFor="marketing-embedded" style={{ fontSize: 14 }}>Receive product updates and tips</label>
              </div>
              {error && <div style={{ color: '#d32f2f', fontWeight: 500, textAlign: 'center', marginTop: 4 }}>{error}</div>}
              <button type="submit" style={{ marginTop: 10, padding: '12px 0', fontSize: 17, background: 'var(--sage-600)', color: '#fff', border: 'none', borderRadius: 12, fontWeight: 600, letterSpacing: 0.5, boxShadow: 'var(--shadow-soft)', cursor: 'pointer' }}>Save</button>
              {saved && <div style={{ color: '#388e3c', fontWeight: 500, textAlign: 'center', marginTop: 8 }}>Profile saved!</div>}
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--mist-100)' }}>
      {/* Mobile hamburger */}
      {isMobile && (
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          style={{ position: 'fixed', top: 18, left: 18, zIndex: 2100, background: 'var(--sage-600)', color: '#fff', border: 'none', borderRadius: 12, padding: '10px 12px', fontSize: 22, boxShadow: 'var(--shadow-soft)', cursor: 'pointer' }}
          aria-label="Open menu"
        >
          ☰
        </button>
      )}
      {/* Sidebar */}
      <nav
        style={
          isMobile
            ? (sidebarOpen ? mobileSidebarOpenStyle : mobileSidebarStyle)
            : sidebarStyle
        }
      >
        <div>
          <div style={{ fontWeight: 700, fontSize: 26, letterSpacing: 1, textAlign: 'center', marginBottom: 38, color: '#fff' }}>ProVault</div>
          {/* User indicator */}
          <div style={{ background: 'var(--sage-600)', color: '#fff', borderRadius: 8, padding: '10px 16px', margin: '0 18px 18px 18px', fontSize: 15, fontWeight: 500, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, boxShadow: 'var(--shadow-soft)' }}>
            <span>Logged in as:</span>
            <span style={{ fontWeight: 700 }}>{currentUser.name}</span>
            <span style={{ fontSize: 13, opacity: 0.85 }}>{currentUser.email}</span>
            {currentUser.role && <span style={{ fontSize: 13, opacity: 0.7 }}>({currentUser.role})</span>}
          </div>
          
          {/* Dashboard */}
          <div style={{ marginBottom: 24 }}>
            <button
              onClick={() => { navigate('/dashboard'); setSidebarOpen(false); }}
              style={{
                background: '#29304a', color: '#fff', border: 'none', borderRadius: 6, padding: '12px 18px', fontWeight: 500, fontSize: 16, margin: '0 18px', width: 'calc(100% - 36px)', cursor: 'pointer', textAlign: 'left', outline: 'none',
              }}
            >
              Dashboard
            </button>
          </div>

          {/* Assets Section */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#888', margin: '0 18px 8px 18px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Assets</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <button
                onClick={() => { navigate('/financial-assets'); setSidebarOpen(false); }}
                style={{
                  background: '#29304a', color: '#fff', border: 'none', borderRadius: 6, padding: '12px 18px', fontWeight: 500, fontSize: 16, margin: '0 18px', cursor: 'pointer', textAlign: 'left', outline: 'none',
                }}
              >
                Financial Assets
              </button>
              <button
                onClick={() => { navigate('/legal-assets'); setSidebarOpen(false); }}
                style={{
                  background: '#29304a', color: '#fff', border: 'none', borderRadius: 6, padding: '12px 18px', fontWeight: 500, fontSize: 16, margin: '0 18px', cursor: 'pointer', textAlign: 'left', outline: 'none',
                }}
              >
                Legal Assets
              </button>
              <button
                onClick={() => { navigate('/other-assets'); setSidebarOpen(false); }}
                style={{
                  background: '#29304a', color: '#fff', border: 'none', borderRadius: 6, padding: '12px 18px', fontWeight: 500, fontSize: 16, margin: '0 18px', cursor: 'pointer', textAlign: 'left', outline: 'none',
                }}
              >
                Other Assets
              </button>
            </div>
          </div>

          {/* Account dropdown */}
          <div style={{ position: 'relative', margin: '32px 18px 0 18px' }}>
            <div
              style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '10px 0', borderRadius: 6, fontWeight: 500, fontSize: 15, color: '#fff', background: accountMenuOpen ? '#29304a' : 'none' }}
              onMouseEnter={() => setAccountMenuOpen(true)}
              onMouseLeave={() => setAccountMenuOpen(false)}
              onClick={() => isMobile && setAccountMenuOpen(!accountMenuOpen)}
            >
              <span style={{ fontSize: 18, marginRight: 4 }}>👤</span>
              Account
              <span style={{ marginLeft: 'auto', fontSize: 14 }}>▼</span>
            </div>
            {accountMenuOpen && (
              <div
                style={{ background: '#fff', color: '#222b45', borderRadius: 8, boxShadow: '0 4px 16px rgba(30,40,60,0.13)', minWidth: 140, zIndex: 10, padding: '8px 0' }}
                onMouseEnter={() => setAccountMenuOpen(true)}
                onMouseLeave={() => setAccountMenuOpen(false)}
              >
                <button
                  onClick={() => { navigate('/profile'); setAccountMenuOpen(false); setSidebarOpen(false); }}
                  style={{ width: '100%', background: 'none', border: 'none', color: '#222b45', fontWeight: 500, fontSize: 15, padding: '10px 18px', textAlign: 'left', cursor: 'pointer', borderRadius: 6, transition: 'background 0.15s' }}
                  onMouseOver={e => e.currentTarget.style.background = '#f0f0f0'}
                  onMouseOut={e => e.currentTarget.style.background = 'none'}
                >
                  Your profile
                </button>
                <button
                  onClick={() => { navigate(currentUser.type === 'nominee' ? '/owners' : '/nominees'); setAccountMenuOpen(false); setSidebarOpen(false); }}
                  style={{ width: '100%', background: 'none', border: 'none', color: '#222b45', fontWeight: 500, fontSize: 15, padding: '10px 18px', textAlign: 'left', cursor: 'pointer', borderRadius: 6, transition: 'background 0.15s' }}
                  onMouseOver={e => e.currentTarget.style.background = '#f0f0f0'}
                  onMouseOut={e => e.currentTarget.style.background = 'none'}
                >
                  {currentUser.type === 'nominee' ? 'Owners' : 'Nominees'}
                </button>
                <button
                  onClick={() => { setCurrentUser(null); navigate('/login'); setSidebarOpen(false); }}
                  style={{ width: '100%', background: 'none', border: 'none', color: '#222b45', fontWeight: 500, fontSize: 15, padding: '10px 18px', textAlign: 'left', cursor: 'pointer', borderRadius: 6, transition: 'background 0.15s' }}
                  onMouseOver={e => e.currentTarget.style.background = '#f0f0f0'}
                  onMouseOut={e => e.currentTarget.style.background = 'none'}
                >
                  Log out
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Main content */}
      <div style={{ flex: 1, marginLeft: isMobile ? 0 : 220, padding: isMobile ? '60px 0 0 0' : '0 0 0 0', minHeight: '100vh', overflow: 'auto' }}>
        <div style={{ padding: 20, position: 'relative' }}>
          <div style={{ maxWidth: 800, margin: '0 auto', background: '#fff', borderRadius: 12, boxShadow: '0 4px 24px rgba(30,40,60,0.10)', padding: 32 }}>
            <h2 style={{ marginBottom: 24, fontWeight: 700, fontSize: 24, color: '#222' }}>Account Profile</h2>
            {/* Profile Details Section */}
            <div style={{ marginBottom: 32 }}>
              <h3 style={{ fontWeight: 600, fontSize: 20, marginBottom: 16 }}>Profile details</h3>
              <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontWeight: 500, color: '#333' }}>First Name</label>
                    <input value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="First Name" style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: '1px solid #ccc', fontSize: 16 }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontWeight: 500, color: '#333' }}>Last Name</label>
                    <input value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Last Name" style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: '1px solid #ccc', fontSize: 16 }} />
                  </div>
                </div>
                <div>
                  <label style={{ fontWeight: 500, color: '#333' }}>Email</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: '1px solid #ccc', fontSize: 16 }} />
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ flex: 1, position: 'relative' }}>
                    <label style={{ fontWeight: 500, color: '#333' }}>New Password</label>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="New Password"
                      style={{ width: '100%', padding: '10px 36px 10px 12px', borderRadius: 6, border: '1px solid #ccc', fontSize: 16, boxSizing: 'border-box' }}
                    />
                    <span
                      onClick={() => setShowPassword(s => !s)}
                      style={{ position: 'absolute', right: 10, top: 38, cursor: 'pointer', color: '#888', fontSize: 18 }}
                      title={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? '🙈' : '👁️'}
                    </span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontWeight: 500, color: '#333' }}>Confirm Password</label>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      placeholder="Confirm Password"
                      style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: '1px solid #ccc', fontSize: 16, boxSizing: 'border-box' }}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <input id="marketing" type="checkbox" checked={marketingConsent} onChange={e => setMarketingConsent(e.target.checked)} />
                  <label htmlFor="marketing" style={{ fontSize: 14 }}>Receive product updates and tips</label>
                </div>
                {error && <div style={{ color: '#d32f2f', fontWeight: 500, textAlign: 'center', marginTop: 4 }}>{error}</div>}
                <button type="submit" style={{ marginTop: 10, padding: '12px 0', fontSize: 17, background: 'var(--sage-600)', color: '#fff', border: 'none', borderRadius: 12, fontWeight: 600, letterSpacing: 0.5, boxShadow: 'var(--shadow-soft)', cursor: 'pointer' }}>Save</button>
                {saved && <div style={{ color: '#388e3c', fontWeight: 500, textAlign: 'center', marginTop: 8 }}>Profile saved!</div>}
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 